<template>
  <div class="flex flex-col px-2">
    <div v-if="$app.props.holdTypes && $app.props.holdTypes.length > 0" class="font-bold p-2 flex">
      Block or Hold Seats
    </div>
    <div v-else class="font-bold p-2 flex">Block</div>
    <template v-if="!hasBookedOrDisabledSeatsInSelection">
      <div v-if="selectedOnlyOneGa">
        <div
          v-if="categorySelectedForGa"
          class="flex gap-2 bg-white hover:bg-gray-100 cursor-pointer border h-8 items-center"
        >
          <SMIcon icon="lock" class="text-sm" />
          <span class="">Block Seats</span>
          <SMNumberInput
            v-model="gaBlockedSeats"
            type="number"
            :min="0"
            :max="gaCapacity"
            @update:modelValue="gaBlockChange($event)"
          />
          <span>/{{ gaCapacity }}</span>
        </div>
        <div v-if="!categorySelectedForGa">
          <div>
            <span><SMIcon icon="warning" /></span>
            <span>Please assign a category to this GA first</span>
          </div>
        </div>
      </div>

      <div v-else-if="selectedOtherThanGa" class="flex flex-col gap-0.5">
        <div
          v-tippy="'Cannot be purchased'"
          class="flex gap-2 bg-white hover:bg-gray-100 cursor-pointer border h-8 items-center"
          @click="lockSeats()"
        >
          <SMIcon icon="lock" class="text-sm" />
          <label class="px-2 cursor-pointer">Block Seats</label>
          <span class="ml-auto mr-4">{{ seatsByHoldType['__default'] }}</span>
        </div>

        <div
          v-for="holdType in $app.props.holdTypes"
          :key="holdType.key"
          class="flex gap-2 bg-white hover:bg-gray-100 cursor-pointer border h-8 items-center"
          @click="lockSeats(holdType.key)"
        >
          <label class="text-lg px-2 font-bold">{{ holdType.key.toUpperCase() }}</label>
          <label class="px-2 cursor-pointer">{{ holdType.value }}</label>
          <span class="ml-auto mr-4">{{ seatsByHoldType[holdType.key] }}</span>
        </div>

        <!-- <div
          class="flex gap-2 bg-white hover:bg-gray-100 cursor-pointer border h-8 items-center mt-2"
          @click="unlockSeats()"
        >
          <SMIcon icon="unlock" />
          <label class="px-2">Clear</label>
        </div> -->
        <SMButton icon="unlock" type="primary" filled class="mt-2" @click="unlockSeats()">Clear</SMButton>
      </div>
    </template>

    <div v-if="$app.props.status === 1" class="py-4">
      <div class="md-subheading px-2 my-2 bg-gray-200">Attributes</div>
      <ul class="space-y-2 px-2 py-1">
        <li>
          <SMInput v-model="meta1" v-tippy="'Meta 1'" icon="1" :placeholder="meta1Label" @change="onChangeMeta1" />
        </li>
        <li>
          <SMInput v-model="meta2" v-tippy="'Meta 2'" icon="2" :placeholder="meta2Label" @change="onChangeMeta2" />
        </li>
      </ul>

      <div class="md-subheading px-2 my-2 bg-gray-200">Visibility</div>
      <ul
        v-if="!hasOnlyUnavailableSeatsSelected"
        class="space-y-2 px-2 py-1 grid gap-2"
        style="grid-template-columns: auto 1fr"
      >
        <li class="contents">
          <SMIcon icon="eye_off" class="text-sm" />
          <SMCheckbox v-model="seatsObstructed" class="md-list-action">Restricted View</SMCheckbox>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import eventbus from 'tiny-emitter/instance';
import { mapState } from 'vuex';
import forOwn from 'lodash/forOwn';
import sumBy from 'lodash/sumBy';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import forEach from 'lodash/forEach';
import SMIcon from '@/components/ui/SMIcon.vue';
import { TYPE, SEATSTATUS } from '@/constants';
import SMInput from '@/components/ui/SMInput.vue';
import SMNumberInput from '@/components/ui/SMNumberInput.vue';
import SMButton from '@/components/ui/SMButton.vue';
import { Seat } from '@/core/models/seat';
import { TableSeat } from '@/core/models/tables/tableSeat';

export default {
  components: {
    SMIcon,
    SMInput,
    SMNumberInput,
    SMButton,
  },
  props: ['selection'],
  data() {
    return {
      gaPos: [],
      posAssignedGa: 0,
      gaBlockedSeats: 0,
      seatsAndTableSeats: [],
      seatsByHoldType: {},
    } as {
      gaPos: any[];
      posAssignedGa: number;
      gaBlockedSeats: number;
      seatsAndTableSeats: (Seat | TableSeat)[];
      seatsByHoldType: { [key: string]: number };
    };
  },
  computed: {
    ...mapState('pos', ['pos', 'assignedList']),
    units() {
      return this.selection.items;
    },
    meta1() {
      const meta1s = map(this.units, (unit) => unit.attributes.meta1);
      const uniqmeta1s = uniq(meta1s);
      if (uniqmeta1s.length > 1) {
        return '';
      } else {
        return uniqmeta1s[0];
      }
    },
    meta2() {
      const meta2s = map(this.units, (unit) => unit.attributes.meta2);
      const uniqmeta2s = uniq(meta2s);
      if (uniqmeta2s.length > 1) {
        return '';
      } else {
        return uniqmeta2s[0];
      }
    },
    meta1Label() {
      const meta1s = map(this.units, (unit) => unit.attributes.meta1);
      const uniqmeta1s = uniq(meta1s);
      if (uniqmeta1s.length > 1) {
        return 'Multiple meta 1 values assigned';
      } else {
        return 'Meta 1';
      }
    },
    meta2Label() {
      const meta2s = map(this.units, (unit) => unit.attributes.meta2);
      const uniqmeta2s = uniq(meta2s);
      if (uniqmeta2s.length > 1) {
        return 'Multiple meta 2 values assigned';
      } else {
        return 'Meta 2';
      }
    },
    seatsObstructed: {
      get: function () {
        return this.units.some((unit) => unit.attributes.obstructed === true);
      },
      set: function (newValue: boolean) {
        this.units.forEach((s) => (s.obstructed = newValue));
      },
    },
    selectedOnlyOneGa() {
      return (
        this.selection.items.length === 1 && this.selection.items[0].type === TYPE.GA && this.$app.props.isManageGA
      );
    },
    selectedOtherThanGa() {
      return !this.selection.items.find((u) => u.type === TYPE.GA);
    },
    hasOnlyUnavailableSeatsSelected() {
      return this.units.every((u) => {
        return u.attributes.status === SEATSTATUS.OCCUPIED || u.attributes.status === SEATSTATUS.RESERVED || u.inCart;
      });
    },
    categorySelectedForGa() {
      const selectedGaAttr = this.selection.items[0].attributes;
      return selectedGaAttr.categoryId !== undefined && selectedGaAttr.categoryId !== '';
    },
    gaCapacity() {
      let gaCapacity = 0;
      if (this.selection.items && this.selection.items.length === 1) {
        const cat = this.$app.getCategoryObj(this.selection.items[0].attributes.categoryId);
        gaCapacity = cat.capacity;
        gaCapacity -= this.posAssignedGa;
      }
      return gaCapacity;
    },
    gaBlocked() {
      return this.selection.items[0].attributes && this.selection.items[0].attributes.blocked
        ? parseInt(this.selection.items[0].attributes.blocked, 10)
        : 0;
    },
    hasBookedOrDisabledSeatsInSelection() {
      let bookedUnits = 0;
      if (this.selection.items.length > 0) {
        this.selection.items.forEach((u) => {
          if (u.type === TYPE.SEAT || u.type === TYPE.TABLESEAT) {
            bookedUnits +=
              u.attributes.status === SEATSTATUS.DISABLED ||
              u.attributes.status === SEATSTATUS.OCCUPIED ||
              u.attributes.status === SEATSTATUS.RESERVED ||
              u.inCart
                ? 1
                : 0;
          } else if (u.type === TYPE.TABLE) {
            const bookedTableSeats = u.seats.filter(
              (s) =>
                s.attributes.status === SEATSTATUS.DISABLED ||
                s.attributes.status === SEATSTATUS.OCCUPIED ||
                s.attributes.status === SEATSTATUS.RESERVED ||
                s.inCart
            );
            bookedUnits += bookedTableSeats.length > 0 ? 1 : 0;
          }
        });
      }
      return !!bookedUnits;
    },
  },
  created() {
    if (this.selectedOnlyOneGa) {
      const { pos } = this.selection.items[0];
      forOwn(pos, (val, key) => {
        if (!val) {
          return;
        }
        this.gaPos[key] = val || 0;
      });
      this.posAssignedGa = sumBy(this.gaPos);
      this.gaBlockedSeats = this.gaBlocked;
    }

    eventbus.on('seats:lock:updated', this.updateBlockedSeatsCount, this);
    this.updateBlockedSeatsCount();
  },
  beforeDestroy() {
    eventbus.off('seats:lock:updated', this.updateBlockedSeatsCount);
  },
  methods: {
    onChangeMeta1(value) {
      this.$app.seatmap.triggerUnsavedChanges(true);
      forEach(this.units, (unit) => unit.changeMeta1(value.trim()));
    },
    onChangeMeta2(value) {
      this.$app.seatmap.triggerUnsavedChanges(true);
      forEach(this.units, (unit) => unit.changeMeta2(value.trim()));
    },
    lockSeats(holdType?: string) {
      eventbus.emit('seats:lock', holdType);
      eventbus.emit('history:snapshot', 'Blocked seats');
    },
    unlockSeats() {
      eventbus.emit('seats:unlock');
      eventbus.emit('history:snapshot', 'Unbocked seats');
    },
    gaBlockChange(val) {
      val = parseInt(val, 10);
      const maxVal = parseInt(this.gaCapacity, 10);
      if (maxVal < val) {
        val = maxVal;
      }
      this.gaBlockedSeats = val;
      this.selection.items[0].attributes.blocked = val;
      eventbus.emit('ga:block:updated');
      eventbus.emit('history:snapshot', 'GA seats blocked');
    },
    updateHolds(blockedSeats: (Seat | TableSeat)[]) {
      const holds: { [key: string]: number } = {};
      blockedSeats.forEach((seat) => {
        if (seat.attributes.holdType) {
          holds[seat.attributes.holdType] = holds[seat.attributes.holdType] ? holds[seat.attributes.holdType] + 1 : 1;
        } else {
          holds['__default'] = holds['__default'] ? holds['__default'] + 1 : 1;
        }
      });

      this.seatsByHoldType = holds;
    },
    updateBlockedSeatsCount() {
      this.seatsAndTableSeats = this.$app.getFilteredSeats(this.$app.currentSection?.uuid);
      this.updateHolds(this.seatsAndTableSeats.filter((s) => s.locked) as (Seat | TableSeat)[]);
    },
  },
};
</script>

<style lang="scss" scoped>
.numeric-input {
  width: 90px !important;
  min-width: auto !important;
}

.ga-blocked .md-avatar {
  margin-left: 5px;
}

.pos-name {
  max-width: 100px;
}

.selected {
  background-color: #4051b4 !important;
  span {
    color: white !important;
  }
}

.md-avatar {
  overflow: visible !important;
}
.color-button {
  min-width: 19px;
  min-height: 32px;
}

.pading-left {
  padding-left: 5px;
}

.no-padding {
  padding: 0 !important;
}

.md-list-item {
  padding: 2px 0;
  border-bottom: 1px solid #eae8e8;
  &:first-child {
    padding-top: 2px;
  }
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background-color: #eaeaea;
  }
}

.md-list.md-dense {
  padding: 0 0 !important;
}
.pos-icon {
  width: 20px;
  &.clickable {
    opacity: 0.4;
    &:hover {
      opacity: 1;
    }
  }
}
.md-input-container {
  justify-content: flex-start !important;
  min-height: auto !important;
  margin: 0 !important;
  padding-top: 0;
  width: 165px;
  display: inline-block !important;
  vertical-align: middle;
}
</style>
